<template>
  <div>
    <LoaderIndicator
      v-if="isLoading"
      class="deposit-method__loader"
      with-container
    />
    <div v-else>
      <NavBar sticky class="deposit-method__nav-bar">
        <template #left>
          <BackButton />
        </template>
      </NavBar>
      <section class="deposit-method__container">
        <div class="deposit-method__content">
          <HeadingDisplay
            class="deposit-method__header"
            :title="staticTexts.title"
            :subtitle="staticTexts.subtitle"
          />
          <HelperViewStatus
            v-if="hasError"
            class="deposit-method__error"
            generic-error
            button-label="Tentar novamente"
            @click="fetchDepositInfo()"
          />
          <ContentDisplay v-else>
            <template #content-block-1>
              <component
                :is="getDepositMethodSection().component"
                :[getDepositMethodSection().prop]="
                  getDepositMethodSection().value
                "
              />
            </template>
          </ContentDisplay>
          <Row
            v-if="depositMethod === 'pix'"
            :has-line="false"
            data-testid="button-ted-view"
            class="deposit-method__row-ted"
            icon="EA0120"
            left-title="Deposite via TED"
            left-subtitle="Transfira em dias úteis das 8h às 17h"
            has-interaction
            chevron
            @click="goToTedView"
          >
            <template #icon-left>
              <InitialAvatarIcon icon="EA0120" size="medium" />
            </template>
          </Row>
        </div>
      </section>
      <PixSectionModal
        :is-open-modal="isOpenModal"
        :message-data="messageData"
        @on-close="closeModal"
      />
      <ButtonContainer
        v-if="depositMethod === 'ted'"
        class="deposit-method__button-container"
      >
        <template #button-primary>
          <ButtonPrimary
            size="large"
            :disabled="hasError"
            :label="copyButtonLabel"
            @click="copyAccountNumber"
          />
        </template>
      </ButtonContainer>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  NavBar,
  HeadingDisplay,
  ButtonContainer,
  ButtonPrimary,
  ContentDisplay,
  LoaderIndicator,
  HelperViewStatus,
  Row,
  InitialAvatarIcon
} from '@warrenbrasil/nebraska-web';
import { BackButton } from '@/modules/common/components/buttons/BackButton';
import { useRoute, useRouter } from 'vue-router/composables';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { TedSection, PixSection, PixSectionModal } from './components';
import { getPixInfo } from '@/modules/cash/services/cash';
import type { IPixInfo } from '@/modules/cash/services/cash';
import { pixES } from '@/modules/cash/services/sse';
import type { IDepositMethodSection, IPixRequestMessage } from './types';
import { headerTexts } from './texts';
import { tradeModule } from '@/modules/trade/store/trade';
import {
  ICustomerBankAccountResponse,
  getCustomerBankAccount,
  getNewCustomerBankAccount
} from '@/modules/transaction/services/new-deposit';
import { FeatureFlagsModule } from '@/modules/common/store/FeatureFlagsModule';
import { FeatureFlags } from '@/types/models/FeatureFlags';

const route = useRoute();
const router = useRouter();

const messageData = ref<IPixRequestMessage['message'] | null>(null);
const hasError = ref(false);
const isLoading = ref(true);
const depositInfo = ref<
  ICustomerBankAccountResponse['bankAccount'] | IPixInfo | null
>(null);
const isOpenModal = ref(false);
const copyButtonLabel = ref('Copiar dados');

const eventSource = pixES();

eventSource.onerror = ev => {
  eventSource.close();
  console.error(ev);
};

eventSource.onmessage = (event: MessageEvent) => {
  const eventData = JSON.parse(event.data);
  if (eventData.message) {
    messageData.value = eventData.message as IPixRequestMessage['message'];
    isOpenModal.value = true;
  }
};

const depositMethod = computed(() => route.meta!.depositMethod);

const staticTexts =
  depositMethod.value === 'ted' ? headerTexts.ted : headerTexts.pix;

const copyAccountNumber = async () => {
  try {
    const { account } =
      depositInfo.value as ICustomerBankAccountResponse['bankAccount'];
    await navigator.clipboard.writeText(account);
    copyButtonLabel.value = 'Copiado!';
  } catch (e) {
    console.error(e);
  } finally {
    setTimeout(() => {
      copyButtonLabel.value = 'Copiar dados';
    }, 2000);
  }
};

async function fetchDepositInfo() {
  try {
    hasError.value = false;
    isLoading.value = true;
    await fetchRequest();
  } catch (e) {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

async function fetchRequest() {
  if (depositMethod.value === 'ted') {
    depositInfo.value = (await getBankAccount()).bankAccount;
  } else {
    depositInfo.value = await getPixInfo();
  }
}

async function getBankAccount() {
  const shouldUseNewRoute = FeatureFlagsModule.isEnabled(
    FeatureFlags.ENABLE_BFF_BANKING_MIGRATION
  );

  if (shouldUseNewRoute) {
    return await getNewCustomerBankAccount();
  } else {
    return await getCustomerBankAccount();
  }
}

function getDepositMethodSection(): IDepositMethodSection {
  if (depositMethod.value === 'ted')
    return {
      component: TedSection,
      prop: 'deposit-information',
      value: depositInfo.value as ICustomerBankAccountResponse['bankAccount']
    };
  return {
    component: PixSection,
    prop: 'pix-info',
    value: depositInfo.value as IPixInfo
  };
}

function closeModal() {
  isOpenModal.value = false;
}

function goToTedView() {
  router.push({ name: 'deposit-ted' });
}

onMounted(async () => {
  await fetchDepositInfo();
  await tradeModule.fetchStatus();
});

onUnmounted(() => {
  eventSource.close();
});
</script>

<style lang="less" scoped>
.deposit-method {
  &__nav-bar,
  &__header,
  &__content {
    margin-bottom: @size-spacing-x800;
  }

  &__loader {
    height: 100vh;
  }

  &__container {
    padding: 0 @size-spacing-x500;

    .to-lg({ padding: 0 @size-spacing-x400; });
  }

  &__error {
    margin: auto;
  }

  &__content {
    max-width: 592px;
    margin: 0 auto @size-spacing-x1800;
  }

  &__row-ted {
    margin-top: @size-spacing-x800;
  }

  &__button-container {
    background-color: @background-secondary;
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 20;
  }
}
</style>
